import * as React from 'react';
import {createFileRoute, useNavigate} from '@tanstack/react-router';
import { TypeAnimation } from 'react-type-animation';
import {addItemToBasket, getSystemConfiguratorBySystemId, saveQuote} from '@/services.ts';
import SystemTotal from '@/components/atoms/SystemTotal.tsx';
import SystemConfiguratorCategory from '@/components/atoms/SystemConfiguratorCategory.tsx';
import type {
  TemplateCategory,
  TemplateCategoryComponent
} from '@/types';
import { Button } from '@/components/atoms/button.tsx';
import { SYSTEMS_STALE_TIME_5_MINS } from '@/config';
import { queryOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { calcPriceDiffs, calcTotal, generateSystemQuote } from '../../../components/helpers/configuratorHelpers.ts';
import { ErrorComponent } from '@/components/molecules/ErrorComponent';
import { BackButton } from '@/components/atoms/BackButton';
import { Loader } from '@/components/molecules/Loader';
import { LoadingScreen } from '@/components/molecules/LoadingScreen';
import SystemImage from '@/components/atoms/SystemImage.tsx';

let addToBasketIsDone = false;

const configuratorQueryOptions = (systemId: string) => queryOptions({
  queryKey: ['systemConfigurator', systemId],
  queryFn: () => getSystemConfiguratorBySystemId(systemId),
  staleTime: SYSTEMS_STALE_TIME_5_MINS,
});

export const Route = createFileRoute('/systems/$systemId/configurator')({
  loader: async({ params, context }) => {
    const { systemId } = params;
    return context.queryClient?.ensureQueryData(configuratorQueryOptions(systemId));
  },
  component: SystemConfigurator,
  pendingComponent: LoadingScreen,
});

function SystemConfigurator() {
  const { systemId } = Route.useParams();
  const [lastChangeTs, setLastChangeTs] = React.useState(Date.now());
  const [isLoading, setIsLoading] = React.useState(false);
  const { error, data } = useSuspenseQuery(configuratorQueryOptions(systemId));
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if(error) {
    return (<ErrorComponent error={error} />)
  }
  if(data) {
    const configurator = data;
    const [systemTotal, setSystemTotalAsCents] = React.useState(calcTotal(configurator));

    const setOption = (componentId: string, categoryId: string) => () => {
      // Mutation Warning
      const templateCategory = configurator.template.categories.find((category: TemplateCategory) =>  category._id === categoryId);
      if(templateCategory) {
        templateCategory.components.forEach((component: TemplateCategoryComponent) => {
          component.isSelected = component._id === componentId;
        });
      }
      setSystemTotalAsCents(calcTotal(configurator));
      calcPriceDiffs(configurator);
      // Using this to trigger a re-render of the configurator when there is no price change
      setLastChangeTs(Date.now());
    }

    const setQty = (categoryId: string) => (qty: number) => {
      // Mutation Warning
      const templateCategory = configurator.template.categories.find((category: TemplateCategory) =>  category._id === categoryId);
      if(templateCategory) {
        templateCategory.qty = qty;
      }
      setSystemTotalAsCents(calcTotal(configurator));
    }

    const handleAddToBasket = () => {
      setIsLoading(true);
      const systemQuote = generateSystemQuote(configurator);
      addItemToBasket(systemQuote)
        .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['basket'],
        })
        .then(() => {
          setTimeout(() => {
            setIsLoading(false);
            navigate({ to: '/basket'});
          }, 1000);
        });
      });

    }
    const handleSaveQuote = () => {
      const systemQuote = generateSystemQuote(configurator);
      saveQuote(systemQuote).then(() => {
        navigate({ to: '/account/saved-quotes'});
      });
    }
    if(isLoading) {
      return (<Loader loadingMessage="Adding to basket..." height={500} />)
    }
    return (
      <div className="mt-4">
        <div className="p-4">
          <BackButton />
          <h1 className="text-3xl font-bold">
            {configurator.template.name}
          </h1>
          <div className="flex flex-row justify-center items-center p-16">
            <SystemImage image={configurator.systemDetails.images[0]} />
          </div>
          <p className="mt-2">
            {configurator.template.description}
          </p>
          <div className="mt-6">
            {
              configurator.template.categories.map(category => {
                return (<SystemConfiguratorCategory setQty={setQty} setOption={setOption} key={category._id} category={category} />)
              })
            }
          </div>
        </div>
        <div className="fixed w-full p-4 bottom-0 min-h-[64px] border-t-1 z-50 bg-white">
          <SystemTotal total={systemTotal} />
          <div className="flex flex-row space-x-2 mb-2">
            <div className="basis-1/2">
              <Button className="w-full" variant="secondary" onClick={handleSaveQuote}>Save Quote</Button>
            </div>
            <div className="basis-1/2">
              <Button className="w-full" onClick={handleAddToBasket}>
                Add to Basket
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }



  return(
    <div>
      <h1>Something went wrong</h1>
    </div>
  )
}
